import React from 'react';
import { Check, HardDrive, Loader2, MessageSquare, Settings } from 'lucide-react';
import Card from '../../components/Card';
import { SettingsFeedback } from './driveSettingsParts';

export function StorageSettingsPanel({
  config,
  feedback,
  handleSave,
  saving,
  setConfig,
  setFeedback,
}) {
  return (
      <Card
        title="Google Drive"
        icon={HardDrive}
      >
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 16 }}>
          Files are stored in Docket (Postgres); Google Drive is an optional published
          mirror. When an admin publishes a contract, the app creates per-counterparty
          folders under this root.
        </div>

        <SettingsFeedback
          feedback={feedback}
          onDismiss={() => setFeedback({ type: '', message: '' })}
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 16,
          }}
        >
          <div>
            <label className="field-label">Shared drive ID</label>
            <input
              type="text"
              className="input"
              value={config.sharedDriveId}
              onChange={(e) => setConfig((prev) => ({ ...prev, sharedDriveId: e.target.value }))}
              placeholder="e.g. 0AJ3Xk..."
            />
            <div className="field-hint">Optional. Leave blank to use the user's My Drive.</div>
          </div>
          <div>
            <label className="field-label">Root folder ID</label>
            <input
              type="text"
              className="input"
              value={config.rootFolderId}
              onChange={(e) => setConfig((prev) => ({ ...prev, rootFolderId: e.target.value }))}
              placeholder="e.g. 1BxiMV..."
            />
            <div className="field-hint">
              Contract folders live at <code className="mono">&lt;root&gt;/&lt;Counterparty&gt;/&lt;ShortName&gt;/</code>.
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 20,
            paddingTop: 16,
            borderTop: '0.5px solid var(--border-primary)',
          }}
        >
          <button onClick={handleSave} disabled={saving} className="btn primary sm">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Save configuration
          </button>
        </div>
      </Card>
  );
}

export function SlackSettingsPanel({
  handleSaveSlack,
  savingSlack,
  setSlackConfig,
  setSlackFeedback,
  slackConfig,
  slackFallbacks,
  slackFeedback,
}) {
  return (
      <Card title="Legal notifications" icon={MessageSquare}>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 16 }}>
          Use Slack channel IDs, not display names. Blank fields fall back to env vars when configured.
        </div>

        <SettingsFeedback
          feedback={slackFeedback}
          onDismiss={() => setSlackFeedback({ type: '', message: '' })}
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 16,
          }}
        >
          <div>
            <label className="field-label">Legal notifications channel</label>
            <input
              type="text"
              className="input mono"
              value={slackConfig.legalNotificationsChannel}
              onChange={(e) => setSlackConfig((prev) => ({ ...prev, legalNotificationsChannel: e.target.value }))}
              placeholder="e.g. C0987654321"
            />
            <div className="field-hint">
              Used for NDA, TPRM, and other Legal workflow notifications.
              {slackFallbacks.legalNotificationsChannel && <> Env fallback: <code className="mono">{slackFallbacks.legalNotificationsChannel}</code>.</>}
            </div>
          </div>
          <div>
            <label className="field-label">Deadline reminders email</label>
            <input
              type="email"
              className="input mono"
              value={slackConfig.legalNotificationsEmail}
              onChange={(e) => setSlackConfig((prev) => ({ ...prev, legalNotificationsEmail: e.target.value }))}
              placeholder="e.g. legal@lightspark.com"
            />
            <div className="field-hint">
              Deadline reminders (expiration, auto-renewal notice, obligations) are also emailed here.
              Blank disables email delivery; other notifications stay Slack-only.
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 20,
            paddingTop: 16,
            borderTop: '0.5px solid var(--border-primary)',
          }}
        >
          <button onClick={handleSaveSlack} disabled={savingSlack} className="btn primary sm">
            {savingSlack ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Save Slack settings
          </button>
        </div>
      </Card>
  );
}

export function AssistantSettingsPanel({
  assistantBounds,
  assistantConfig,
  assistantFeedback,
  handleSaveAssistant,
  savingAssistant,
  setAssistantConfig,
  setAssistantFeedback,
}) {
  return (
      <Card title="Assistant behavior" icon={Settings}>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 16 }}>
          Controls shared by the Slack bot and local CLI assistant.
        </div>

        <SettingsFeedback
          feedback={assistantFeedback}
          onDismiss={() => setAssistantFeedback({ type: '', message: '' })}
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
          {[
            {
              key: 'searchResultLimit',
              label: 'Search matches',
              hint: 'Maximum contract matches fetched before answer selection.',
              bounds: assistantBounds.resultLimits,
            },
            {
              key: 'clarificationChoiceLimit',
              label: 'Clarification choices',
              hint: 'Maximum numbered options shown when a contract lookup is ambiguous.',
              bounds: assistantBounds.resultLimits,
            },
            {
              key: 'expiringContractsLimit',
              label: 'Expiring-contract rows',
              hint: 'Maximum rows shown for “what expires soon” questions.',
              bounds: assistantBounds.resultLimits,
            },
            {
              key: 'nextStepsLimit',
              label: 'Outcome next steps shown',
              hint: 'Maximum recommendation bullets shown for intake outcomes that do not generate documents.',
              bounds: assistantBounds.nextStepsLimit,
            },
          ].map((field) => (
            <div key={field.key}>
              <label className="field-label">{field.label}</label>
              <input
                type="number"
                className="input"
                min={field.bounds.min}
                max={field.bounds.max}
                value={assistantConfig[field.key]}
                onChange={(e) => setAssistantConfig((prev) => ({ ...prev, [field.key]: e.target.value }))}
              />
              <div className="field-hint">
                {field.hint} Range: {field.bounds.min}-{field.bounds.max}.
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 20,
            paddingTop: 16,
            borderTop: '0.5px solid var(--border-primary)',
          }}
        >
          <button onClick={handleSaveAssistant} disabled={savingAssistant} className="btn primary sm">
            {savingAssistant ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Save assistant settings
          </button>
        </div>
      </Card>
  );
}
